import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, AlertTriangle, Trophy, Target, CalendarClock, Flame, ChevronRight } from 'lucide-react';
import { formatCurrency } from '../mock';

const fmt = (v, f) => (f === 'money' ? formatCurrency(v || 0) : f === 'pct' ? `${v ?? 0}%` : (v ?? 0));
const TONE = { ok: 't-ok', warn: 't-warn', bad: 't-bad' };

const COLS = { 3: 'xl:grid-cols-3', 4: 'xl:grid-cols-4', 5: 'xl:grid-cols-5', 6: 'xl:grid-cols-6' };

export const KpiRibbon = ({ kpis = [] }) => (
  <div className={`grid grid-cols-2 md:grid-cols-3 ${COLS[kpis.length] || 'xl:grid-cols-6'} gap-px bg-border border border-border rounded-sm overflow-hidden`} data-testid="dash-kpis">
    {kpis.map((k) => (
      <div key={k.label} className="bg-card px-3 py-2.5" data-testid={`kpi-${k.label.replace(/\s+/g, '-').toLowerCase()}`}>
        <p className="ent-label">{k.label}</p>
        <p className={`ent-value mt-1 text-[19px] ${TONE[k.tone] || ''}`}>{fmt(k.value, k.format)}</p>
        {k.delta != null && (
          <p className="mt-0.5 text-[10.5px]">
            <span className={`font-semibold ${k.delta >= 0 ? 't-ok' : 't-bad'}`}>{k.delta >= 0 ? '▲' : '▼'} {Math.abs(k.delta)}%</span>
            <span className="t-label ml-1.5">vs mês anterior</span>
          </p>
        )}
        {!!k.hint && k.delta == null && <p className="mt-0.5 text-[10.5px] t-label">{k.hint}</p>}
      </div>
    ))}
  </div>
);

export const Panel = ({ title, icon: Icon, action, children, testid }) => (
  <div className="ent-panel p-3.5" data-testid={testid}>
    <div className="flex items-center justify-between gap-2 mb-2.5">
      <p className="ent-label flex items-center gap-1.5">{Icon && <Icon className="w-3.5 h-3.5 text-primary" />}{title}</p>
      {action}
    </div>
    {children}
  </div>
);

export const DeptCards = ({ departments = [] }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3" data-testid="dash-departments">
    {departments.map((d) => (
      <Link key={d.key} to={d.to} data-testid={`dept-${d.key}`}
        className="ent-panel p-3.5 hover:border-primary/50 transition-colors group">
        <div className="flex items-center gap-2 mb-2.5">
          <span className={`w-2 h-2 rounded-full ${d.health === 'ok' ? 'bg-emerald-400' : d.health === 'warn' ? 'bg-amber-400' : 'bg-red-400'} animate-pulse-dot`} />
          <p className="text-[12.5px] font-black uppercase tracking-wide">{d.label}</p>
          <ChevronRight className="w-3.5 h-3.5 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
        <div className="space-y-1">
          {d.metrics.map(([label, value, f]) => (
            <div key={label} className="flex items-center justify-between gap-2">
              <span className="text-[11px] t-label truncate">{label}</span>
              <span className="text-[12.5px] font-semibold num">{fmt(value, f)}</span>
            </div>
          ))}
        </div>
      </Link>
    ))}
  </div>
);

export const AlertCenter = ({ alerts = [] }) => (
  <Panel title={`Central de alertas (${alerts.length})`} icon={AlertTriangle} testid="dash-alerts">
    {alerts.length ? (
      <div className="space-y-1.5">
        {alerts.map((a, i) => (
          <Link key={i} to={a.to} data-testid={`alert-${i}`}
            className={`flex items-center gap-2 rounded-md border px-2.5 py-2 transition-colors hover:bg-muted/30 ${a.tone === 'bad' ? 'border-[hsl(var(--bad))]/40' : 'border-[hsl(var(--warn))]/40'}`}>
            <AlertTriangle className={`w-3.5 h-3.5 ${TONE[a.tone]}`} />
            <span className="text-[12.5px] font-medium flex-1">{a.label}</span>
            <span className="text-[11px] t-label">{a.hint}</span>
            <ChevronRight className="w-3.5 h-3.5 t-label" />
          </Link>
        ))}
      </div>
    ) : <p className="text-[12px] t-ok">Nenhum alerta — operação em dia.</p>}
  </Panel>
);

export const SellerRanking = ({ rows = [], me, position, total }) => (
  <Panel title="Ranking do mês" icon={Trophy} testid="dash-ranking"
    action={position ? <span className="ent-tag t-warn" data-testid="my-position">Você: {position}º de {total}</span> : null}>
    {rows.length ? (
      <table className="ent-table">
        <thead><tr><th style={{ width: 28 }}>#</th><th>Vendedor</th><th className="text-right">Vendas</th><th className="text-right">Faturamento</th><th className="text-right">Gross</th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.vendedor} className={r.vendedor === me ? 'bg-primary/[0.07]' : ''}>
              <td className="num t-label">{r.pos}</td>
              <td className="font-medium">{r.vendedor}{r.vendedor === me && <span className="ent-tag ml-1.5">você</span>}</td>
              <td className="num text-right">{r.vendas}</td>
              <td className="num text-right">{formatCurrency(r.faturamento)}</td>
              <td className="num text-right t-ok">{formatCurrency(r.lucro)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    ) : <p className="text-[12px] t-label">Nenhuma venda registrada no mês.</p>}
  </Panel>
);

export const CountList = ({ title, icon, data = {}, empty = 'Sem dados' }) => (
  <Panel title={title} icon={icon}>
    {Object.keys(data).length ? (
      <div className="space-y-1.5">
        {Object.entries(data).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
          <div key={k} className="flex items-center gap-2">
            <span className="text-[12px] flex-1 truncate">{k}</span>
            <div className="h-1.5 rounded-full bg-primary/70" style={{ width: `${Math.min(100, v * 12)}px` }} />
            <span className="text-[12px] font-semibold num w-7 text-right">{v}</span>
          </div>
        ))}
      </div>
    ) : <p className="text-[12px] t-label">{empty}</p>}
  </Panel>
);

export const HotLeads = ({ leads = [] }) => (
  <Panel title="Leads quentes" icon={Flame} testid="dash-hot-leads"
    action={<Link to="/app/crm" className="ent-btn ent-btn-sm">Abrir CRM</Link>}>
    {leads.length ? (
      <div className="space-y-1.5">
        {leads.map((l) => (
          <Link key={l.id} to={`/app/crm?lead=${l.id}`} className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-muted/30 transition-colors">
            <span className="w-1.5 h-1.5 rounded-full bg-red-400" />
            <span className="text-[12.5px] font-medium flex-1 truncate">{l.nome}</span>
            <span className="text-[11px] t-label truncate max-w-[120px]">{l.interesse || '—'}</span>
            <span className="ent-tag">{l.etapa}</span>
          </Link>
        ))}
      </div>
    ) : <p className="text-[12px] t-label">Nenhum lead quente agora.</p>}
  </Panel>
);

export const MyDeals = ({ deals = [] }) => (
  <Panel title="Meus deals em andamento" icon={Target} testid="dash-my-deals">
    {deals.length ? (
      <table className="ent-table">
        <thead><tr><th>Cliente</th><th>Veículo</th><th>Etapa</th></tr></thead>
        <tbody>
          {deals.map((d) => (
            <tr key={d.id}>
              <td className="font-medium max-w-[140px] truncate">{d.customer_name}</td>
              <td className="max-w-[170px] truncate text-foreground/75">{d.vehicle_desc}</td>
              <td><span className="ent-tag">{d.status}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    ) : <p className="text-[12px] t-label">Nenhum deal em andamento.</p>}
  </Panel>
);

export const Appointments = ({ items = [] }) => (
  <Panel title="Próximos compromissos" icon={CalendarClock} testid="dash-appointments"
    action={<Link to="/app/agenda" className="ent-btn ent-btn-sm">Agenda</Link>}>
    {items.length ? (
      <div className="space-y-1.5">
        {items.map((a) => (
          <div key={a.id} className="flex items-center gap-2">
            <span className="num text-[11px] t-label w-[78px]">{a.data} {a.hora}</span>
            <span className="text-[12.5px] font-medium flex-1 truncate">{a.cliente || a.titulo}</span>
            <span className="ent-tag">{a.tipo}</span>
          </div>
        ))}
      </div>
    ) : <p className="text-[12px] t-label">Nenhum compromisso agendado.</p>}
  </Panel>
);

export const RevenueTrendLink = ({ delta }) => (
  <span className="flex items-center gap-1.5 text-xs font-semibold text-emerald-400 bg-emerald-400/10 border border-emerald-400/20 px-3 py-1.5 rounded-full">
    <ArrowUpRight className="w-3.5 h-3.5" /> {delta ?? 0}% vs mês anterior
  </span>
);
